import { useRecoilCallback } from "recoil";
import { LegalEntitiesState, legalEntitiesState } from "../store/legalEntities.store";
import { Entity, FolderNode, FolderTree } from "../../api-client-nswag/custompermission-client";
import { CountryGroupFolderName, ScheduleCustomPermissionStatusesEnum } from "../../constants";

export const useLegalEntitiesActions = () => {

  const loadFromApi = useRecoilCallback(
    ({ set }) =>
      (payload: { Entities: any[]; UsersPerSite: any[], folderPermission: any[] }) => {
        set(legalEntitiesState,
          (prev) => ({
            ...prev,
            entities: payload.Entities,
            usersPerSite: payload.UsersPerSite,
            snapshotEntities: payload.Entities,
            folderPermission: payload.folderPermission,
            folderPermissionSnapshot: payload.folderPermission,
          })
        );
      },
    [],
  );

  const loadFromRefreshFolderPermissions = useRecoilCallback(
    ({ set }) =>
      (payload: { Entities: Entity[]; folderPermission?: FolderTree; preservedEntities?: Entity[] }) => {
        set(legalEntitiesState, (prev) => {
          const allUsers = prev.usersPerSite.map((user) => String(user.userId));
          const preservedEntities = new Map(
            (payload.preservedEntities ?? []).map((entity) => [entity.entityId, entity])
          );
          const normalizeUnrestrictedFolders = (tree: FolderTree): FolderTree =>
            tree.map((node) => {
              const children = normalizeUnrestrictedFolders(node.children ?? []);
              return node.isRestricted === false
                ? {
                  ...node,
                  users: allUsers,
                  usersCount: allUsers.length,
                  children,
                }
                : { ...node, children };
            });

          return {
            ...prev,
            entities: payload.Entities.map((entity) => {
              const preservedEntity = preservedEntities.get(entity.entityId);
              return {
                ...entity,
                ...(entity.isRestricted === false
                  ? { userList: allUsers, numberOfUsers: allUsers.length }
                  : {}),
                ...(preservedEntity
                  ? {
                    isRestricted: preservedEntity.isRestricted,
                    userList: preservedEntity.userList,
                    numberOfUsers: preservedEntity.numberOfUsers,
                  }
                  : {}),
                folderTree: normalizeUnrestrictedFolders(entity.folderTree ?? []),
              };
            }),

            ...(payload.folderPermission !== undefined
              ? { folderPermission: normalizeUnrestrictedFolders(payload.folderPermission),}
              : {}),
          };
        });
      },
    [],
  );

  const updateIsButtonDisabled = useRecoilCallback(
    ({ set }) =>
      (payload: { status: string | null }) => {
        const allowedStatuses = [
          String(ScheduleCustomPermissionStatusesEnum.Cancelled),
          String(ScheduleCustomPermissionStatusesEnum.Verified),
          String(ScheduleCustomPermissionStatusesEnum.NotScheduled),
          null,
        ];
        const statusString = payload.status;
        const isDisabled = !allowedStatuses.includes(statusString);
        set(legalEntitiesState, (prev) => ({
          ...prev,
          isButtonDisabled: isDisabled,
        }));
      },
    [],
  );

  const updateFolderPermission = useRecoilCallback(
    ({ set }) =>
      (payload: { folderPermission: FolderNode; usersPerSite?: any[]; isCSFlow?: boolean }) => {
        const root = payload?.folderPermission;
        if (!root) return;

        set(legalEntitiesState, (prev: LegalEntitiesState) => {
          const effectiveUsersPerSite = Array.isArray(payload.usersPerSite)
            ? payload.usersPerSite
            : prev.usersPerSite;

          const allUsers: string[] = Array.isArray(effectiveUsersPerSite)
            ? effectiveUsersPerSite.map((u) => String(u.userId))
            : [];

          const siteUserIdSet = new Set(allUsers.map(String));
          const filterToSiteUsers = (ids: any): string[] => {
            const arr = Array.isArray(ids) ? ids : [];
            return arr.map(String).filter(id => siteUserIdSet.has(String(id)))
          };

          const hasAllSiteUsers = (users: Array<string | number> | undefined | null): boolean => {
            const filteredUsers = filterToSiteUsers(users);
            const userSet = new Set(filteredUsers.map(String));

            return (
              userSet.size === siteUserIdSet.size &&
              allUsers.every(userId => userSet.has(userId))
            );
          };

          const entityIndex = new Map<number, Entity>(
            Array.isArray(prev.entities)
              ? prev.entities.map((e) => [e.entityId, e])
              : []
          );

          const findMaxCountryGroupFolderCount = (node: FolderNode): number => {
            let maxCount = 0;

            const isCountryGroupName = CountryGroupFolderName.some(name => node?.folderName?.toLowerCase().includes(name.toLowerCase()));

            if (isCountryGroupName) {
              const childrenCount = Array.isArray(node.children)
                ? node.children.length
                : 0;

              maxCount = childrenCount;
            }

            for (const child of node.children ?? []) {
              maxCount = Math.max(
                maxCount,
                findMaxCountryGroupFolderCount(child)
              );
            }

            return maxCount;
          };
          const countryGroupFolderCount = payload?.isCSFlow ? findMaxCountryGroupFolderCount(root) : 0;
          const isCountryGroupRoot = (node: FolderNode): boolean => {
            if (!payload?.isCSFlow) {
              return false;
            }

            const folderNameMatches =
              CountryGroupFolderName.some((name) =>
                node?.folderName
                  ?.toLowerCase()
                  .includes(name.toLowerCase())
              );

            if (!folderNameMatches) {
              return false;
            }

            const childrenCount = Array.isArray(node.children)
              ? node.children.length
              : 0;

            return (childrenCount > 0 && childrenCount === countryGroupFolderCount);
          };

          const cloneFolderTreeForEntity = (node: FolderNode, parentIsCountryGroup = false): FolderNode => {
            const requestedEntityId = Number(node.entityId);
            const hasEntityId = Number.isFinite(requestedEntityId);

            const owner = requestedEntityId !== 0
              ? entityIndex.get(requestedEntityId)
              : undefined;

            const normalizedEntityId =
              hasEntityId &&
                (requestedEntityId === 0 || owner)
                ? requestedEntityId
                : 0;

            const isEntityFolder =
              normalizedEntityId !== 0;

            const isCustomFolder =
              normalizedEntityId === 0;

            /*
             * Existing Country Group propagation remains unchanged.
             */
            const isCurrentCountryGroup =
              isCountryGroupRoot(node);

            const isCountryGroup =
              parentIsCountryGroup ||
              isCurrentCountryGroup;

            const allUsersSet = new Set(allUsers);

            const filteredNodeUsers = (
              node.users ?? []
            ).filter(user =>
              allUsersSet.has(String(user))
            );

            /*
             * Existing override logic remains unchanged.
             */
            const shouldOverride =
              (
                isCustomFolder ||
                (!!owner && owner.isRestricted === false)
              ) &&
              allUsers.length > 0;

            /*
             * Process every child independently.
             *
             * A restricted child does not update the parent's
             * isRestricted value.
             */
            const nextChildren =
              node.children?.map(child =>
                cloneFolderTreeForEntity(
                  child,
                  isCountryGroup
                )
              ) ?? [];

            let users = shouldOverride
              ? allUsers
              : filteredNodeUsers;

            let usersCount = shouldOverride
              ? allUsers.length
              : filteredNodeUsers.length;

            let isRestricted = node.isRestricted;

            /*
             * Existing custom-folder restriction logic remains.
             *
             * This checks only the current folder.
             */
            if (isCustomFolder) {
              const hasNodeUsers =
                filteredNodeUsers.length > 0;

              const isFullAccess =
                node.usersCount === allUsers.length;

              if (
                (!hasNodeUsers || usersCount === 0) &&
                node.isRestricted !== true
              ) {
                users = allUsers;
                usersCount = allUsers.length;
                isRestricted = false;
              } else if (
                hasNodeUsers &&
                isFullAccess
              ) {
                isRestricted =
                  node.isRestricted ?? false;
              } else {
                users = filteredNodeUsers;
                usersCount = filteredNodeUsers.length;
                isRestricted = true;
              }

              /*
               * Important:
               *
               * The previous childRestricted logic has been removed.
               * Therefore, a restricted child will not restrict its
               * parent custom folder or its siblings.
               */
            }

            return {
              ...node,
              entityId: normalizedEntityId,
              users,
              usersCount,
              isRestricted: isCustomFolder
                ? isRestricted
                : node.isRestricted,
              isCountryGroup,
              children: nextChildren,
            };
          };

          /*
           * Existing parent inheritance logic remains unchanged.
           */
          const inheritFromParent = (
            node: FolderNode,
            parent?: FolderNode
          ): FolderNode => {
            const nodeUsers = filterToSiteUsers(
              node?.users
            );

            const parentUsers = filterToSiteUsers(
              parent?.users
            );

            const nodeUsersCount = nodeUsers.length;
            const parentUsersCount = parentUsers.length;

            const parentRestricted =
              parent?.isRestricted === true;

            /*
             * Newly added folder.
             */
            const isNewFolder =
              node?.isRestricted === false &&
              nodeUsersCount === 0;

            let effectiveUsers = nodeUsers;
            let effectiveUsersCount = nodeUsersCount;
            let effectiveIsRestricted =
              node?.isRestricted;

            if (
              isNewFolder &&
              parent &&
              parent?.parentFolderId !== 0
            ) {
              /*
               * Inherit users from the immediate parent.
               */
              if (parentUsersCount > 0) {
                effectiveUsers = [...parentUsers];
                effectiveUsersCount =
                  parentUsersCount;
              }

              /*
               * Inherit restriction from the immediate parent
               * only for a newly added folder.
               */
              effectiveIsRestricted =
                parentRestricted;
            }

            const nextChildren = (
              node.children ?? []
            ).map(child =>
              inheritFromParent(child, {
                ...node,
                users: effectiveUsers,
                usersCount: effectiveUsersCount,
                isRestricted:
                  effectiveIsRestricted,
              } as FolderNode)
            );

            return {
              ...node,
              users: effectiveUsers,
              usersCount: effectiveUsersCount,
              isRestricted:
                effectiveIsRestricted,
              children: nextChildren,
            };
          };

          const updatedSiteTree =
            inheritFromParent(
              cloneFolderTreeForEntity(root)
            );

          /*
           * This only checks whether the entity contains a
           * restricted folder.
           *
           * It does not modify the parent or sibling folders.
           */
          const hasRestrictedFolder = (
            nodes: FolderNode[] | undefined
          ): boolean => {
            return (nodes ?? []).some(node => {
              if (node.isRestricted === true) {
                return true;
              }

              return hasRestrictedFolder(
                node.children
              );
            });
          };

          const updateEntityTree = (
            entityList: Entity[]
          ): Entity[] => {
            return entityList.map(entity => {
              const filteredEntityUserList =
                filterToSiteUsers(
                  entity?.userList ?? []
                );

              /*
               * Preserve the existing entity-user handling.
               */
              const newEntity: Entity =
                !entity.isRestricted
                  ? {
                    ...entity,
                    userList: allUsers,
                    numberOfUsers:
                      allUsers.length,
                    folderTree:
                      entity.folderTree ??
                      undefined,
                  }
                  : {
                    ...entity,
                    userList:
                      filteredEntityUserList,
                    numberOfUsers:
                      filteredEntityUserList.length,
                  };

              const entityRootNode =
                updatedSiteTree.children?.find(
                  child =>
                    child.entityId ===
                    entity.entityId
                );

              if (!entityRootNode) {
                return newEntity;
              }

              /*
               * Preserve the existing folder-tree cloning and
               * Country Group handling.
               */
              const updatedFolderTreeChildren =
                entityRootNode.children?.map(
                  child =>
                    cloneFolderTreeForEntity(
                      child,
                      entityRootNode.isCountryGroup ===
                      true
                    )
                ) ?? [];

              const nextFolderRoot: FolderNode = {
                ...entityRootNode,
                children:
                  updatedFolderTreeChildren,
              };

              /*
               * Entity summary status:
               *
               * 1. Restricted when its own user list is incomplete.
               * 2. Restricted when any folder inside it is restricted.
               *
               * This does not change the restriction status of
               * parent folders or siblings.
               */
              const entityUsersRestricted =
                !hasAllSiteUsers(
                  newEntity.userList
                );

              const entityHasRestrictedFolder =
                hasRestrictedFolder([
                  nextFolderRoot,
                ]);

              const entityRestricted =
                entityUsersRestricted ||
                entityHasRestrictedFolder;

              return {
                ...newEntity,
                isRestricted:
                  entityRestricted,
                folderTree: [nextFolderRoot],
              };
            });
          };

          return {
            ...prev,
            folderPermission: [
              updatedSiteTree,
            ],
            folderPermissionSnapshot: [
              updatedSiteTree,
            ],
            entities: updateEntityTree(
              prev.entities
            ),
            snapshotEntities: updateEntityTree(
              prev.snapshotEntities
            ),
          };
        });
      },
    []
  );

  const updateEntityUserList = useRecoilCallback(
    ({ set }) =>
      (entityId: number, newUserList: string[]) => {
        set(legalEntitiesState, (prev) => {
          const entities: Entity[] = prev?.entities ?? [];

          const nextEntities = entities.map((e) => {
            if (e.entityId !== entityId) return e;

            const prevUserList = e.userList ?? [];
            const newSet = new Set(newUserList);

            const removed = prevUserList.filter((u) => !newSet.has(u));
            const toRemove = new Set(removed);

            const addedUsers = newUserList.filter((u) => !prevUserList.includes(u));
            const toAdd = new Set(addedUsers);

            const reconcileNode = (node: FolderNode): FolderNode => {
              const startingUsers = node.users ?? [];

              let users = toRemove.size
                ? startingUsers.filter((u) => !toRemove.has(u))
                : startingUsers;

              if (toAdd.size) {
                const additions = Array.from(toAdd).filter((u) => !users.includes(u));
                if (additions.length) {
                  users = [...users, ...additions];
                }
              }

              const children = (node.children ?? []).map(reconcileNode);
              return {
                ...node,
                users,
                usersCount: users.length,
                children,
              };
            };

            const folderTree = e.folderTree ?? [];
            let nextFolderTree: FolderNode[] = folderTree;

            if (toRemove.size > 0 || toAdd.size > 0) {
              nextFolderTree = folderTree.map(reconcileNode);
            }

            return {
              ...e,
              userList: newUserList,
              numberOfUsers: newUserList.length,
              isRestricted: e.isRestricted,
              folderTree: nextFolderTree,
            };
          });

          return { ...prev, entities: nextEntities };
        });
      },
    []
  );

  const updateEntityUserListFolderPermission = useRecoilCallback(
    ({ set }) =>
      (entityId: number, newUserList: string[]) => {
        set(legalEntitiesState, (prev) => {

          const folderPermission: FolderTree = prev?.folderPermission ?? [];
          if (!folderPermission.length) {
            return prev;
          }

          const parent = folderPermission[0];
          const parentChildren = parent?.children ?? [];

          const nextChildren = parentChildren.map((e) => {
            if (e.entityId !== entityId) return e;

            const prevUserList = e.users ?? [];
            const newSet = new Set(newUserList);

            const removed = prevUserList.filter((u) => !newSet.has(u));
            const toRemove = new Set(removed);

            const addedUsers = newUserList.filter((u) => !prevUserList.includes(u));
            const toAdd = new Set(addedUsers);

            const reconcileNode = (node: FolderNode): FolderNode => {
              const startingUsers = node.users ?? [];

              let users = toRemove.size
                ? startingUsers.filter((u) => !toRemove.has(u))
                : startingUsers;

              if (toAdd.size) {
                const additions = Array.from(toAdd).filter((u) => !users.includes(u));
                if (additions.length) {
                  users = [...users, ...additions];
                }
              }

              const children = (node.children ?? []).map(reconcileNode);

              return {
                ...node,
                users,
                usersCount: users.length,
                children,
              };
            };

            const folderTree = e.children ?? [];
            const nextFolderTree =
              toRemove.size > 0 || toAdd.size > 0
                ? folderTree.map(reconcileNode)
                : folderTree;

            return {
              ...e,
              users: newUserList,
              usersCount: newUserList.length,
              children: nextFolderTree,
            };
          });

          const nextFolderPermission = [...folderPermission];
          nextFolderPermission[0] = {
            ...parent,
            children: nextChildren,
          };

          return {
            ...prev,
            folderPermission: nextFolderPermission,
          };
        });
      },
    []
  );

  const updateEntityFolderTree = useRecoilCallback(
    ({ set }) =>
      (entityId: number, newFolderTree: FolderTree) => {
        set(legalEntitiesState, (prev) => {
          const updatedEntities = prev.entities.map((e) =>
            e.entityId === entityId ? { ...e, folderTree: newFolderTree } : e
          );

          const updatedFolderPermission = (prev.folderPermission ?? []).map((siteNode) => {
            if (!siteNode.children?.length) return siteNode;

            const updatedChildren = siteNode.children.map((childNode) => {
              if (childNode.entityId === entityId) {
                return { ...newFolderTree[0] };
              }
              return childNode;
            });

            return { ...siteNode, children: updatedChildren };
          });

          return {
            ...prev,
            entities: updatedEntities,
            folderPermission: updatedFolderPermission,
          };
        });
      },
    []
  );

  const updateSiteFolderPermission = useRecoilCallback(
    ({ set }) =>
      (updatedSiteTree: FolderTree) => {
        set(legalEntitiesState, (prev) => {
          if (!Array.isArray(updatedSiteTree)) return prev;

          const annotate = (nodes: FolderNode[]): FolderNode[] =>
            nodes.map((node) => {
              const children = node.children?.length ? annotate(node.children) : [];
              if (node.entityId === 0) {
                return {
                  ...node,
                  children,
                };
              }

              return children === node.children ? node : { ...node, children };
            });

          const nextFolderPermission: FolderTree = annotate(updatedSiteTree);

          const findEntityNode = (nodes: FolderNode[], entityId: number): FolderNode | null => {
            for (const n of nodes) {
              if (n.entityId === entityId) return n;
              if (n.children?.length) {
                const found = findEntityNode(n.children, entityId);
                if (found) return found;
              }
            }
            return null;
          };

          const updatedEntities = (prev?.entities ?? []).map((e) => {
            const node = findEntityNode(nextFolderPermission, e.entityId);
            return node ? { ...e, folderTree: [node] } : e;
          });

          return {
            ...prev,
            folderPermission: nextFolderPermission,
            entities: updatedEntities,
          };
        });
      },
    []
  );

  const resetEntitiesToSnapshot = useRecoilCallback(
    ({ set }) =>
      () => {
        set(legalEntitiesState, (prev) => ({
          ...prev,
          entities: prev.snapshotEntities,
          folderPermission: prev.folderPermissionSnapshot,
        }));
      },
    [],
  );

  const resetEntitiesState = useRecoilCallback(
    ({ set }) =>
      () => {
        set(legalEntitiesState, (prev) => ({
          ...prev,
          entities: [],
          snapshotEntities: [],
          folderPermission: [],
          folderPermissionSnapshot: [],
          usersPerSite: [],
        }));
      },
    [],
  );

  const revalidateEntityIsRestricted = useRecoilCallback(
    ({ set, snapshot }) =>
      async (entityId: number) => {
        const entitiesState = await snapshot.getPromise(
          legalEntitiesState
        );

        const entities: Entity[] =
          entitiesState?.entities ?? [];

        const folderPermission: FolderTree =
          entitiesState?.folderPermission ?? [];

        const allUsers: string[] = (
          entitiesState?.usersPerSite ?? []
        ).map(user => String(user.userId));

        const siteUserSet = new Set(allUsers);
        const siteCount = siteUserSet.size;

        const isCustomFolder = (
          node: FolderNode
        ): boolean => Number(node.entityId) === 0;

        const hasAllSiteUsers = (
          users:
            | Array<string | number>
            | undefined
            | null
        ): boolean => {
          const folderUserSet = new Set(
            (users ?? []).map(String)
          );

          return (
            folderUserSet.size === siteUserSet.size &&
            allUsers.every(userId =>
              folderUserSet.has(userId)
            )
          );
        };

        const getUniqueUserCount = (
          users:
            | Array<string | number>
            | undefined
            | null
        ): number =>
          new Set((users ?? []).map(String)).size;

        /**
         * Custom-folder logic.
         *
         * A custom folder is restricted when:
         * 1. Its custom-folder ancestor is restricted, or
         * 2. It does not contain all site users.
         *
         * Entity folders encountered below a custom folder are preserved.
         */
        const updateCustomFolderTree = (
          tree: FolderTree,
          ancestorRestricted = false
        ): FolderTree => {
          let changed = false;

          const updatedTree = (tree ?? []).map(node => {
            if (!isCustomFolder(node)) {
              return node;
            }

            const currentFolderRestricted =
              ancestorRestricted ||
              !hasAllSiteUsers(node.users);

            const currentChildren =
              node.children ?? [];

            const nextChildren =
              updateCustomFolderTree(
                currentChildren,
                currentFolderRestricted
              );

            const nextUsersCount =
              getUniqueUserCount(node.users);

            const childrenChanged =
              nextChildren !== currentChildren;

            if (
              node.isRestricted ===
              currentFolderRestricted &&
              node.usersCount === nextUsersCount &&
              !childrenChanged
            ) {
              return node;
            }

            changed = true;

            return {
              ...node,
              usersCount: nextUsersCount,
              isRestricted:
                currentFolderRestricted,
              children: nextChildren,
            };
          });

          return changed ? updatedTree : tree;
        };

        /**
         * Entity-folder logic.
         *
         * Entity folders retain the original forceAll behavior. Custom-folder
         * descendants use their own custom-folder restriction calculation.
         */
        const updateEntityFolderTree = (
          tree: FolderTree,
          forceAll: boolean
        ): FolderTree => {
          let changed = false;

          const updatedTree = (tree ?? []).map(node => {
            /*
             * Custom folders use custom-folder validation and do not have
             * their users replaced by the entity-folder forceAll logic.
             */
            if (isCustomFolder(node)) {
              const updatedCustomTree =
                updateCustomFolderTree(
                  [node],
                  false
                );

              const updatedCustomFolder =
                updatedCustomTree[0];

              if (updatedCustomFolder !== node) {
                changed = true;
              }

              return updatedCustomFolder;
            }

            /*
             * This is an entity folder. Preserve the existing entity-folder
             * forceAll behavior.
             */
            const currentChildren =
              node.children ?? [];

            const nextChildren =
              updateEntityFolderTree(
                currentChildren,
                forceAll
              );

            const childrenChanged =
              nextChildren !== currentChildren;

            if (forceAll) {
              const alreadyValid =
                hasAllSiteUsers(node.users) &&
                node.usersCount === siteCount &&
                node.isRestricted === false &&
                !childrenChanged;

              if (alreadyValid) {
                return node;
              }

              changed = true;

              return {
                ...node,
                users: allUsers,
                usersCount: siteCount,
                isRestricted: false,
                children: nextChildren,
              };
            }

            if (
              node.isRestricted === true &&
              !childrenChanged
            ) {
              return node;
            }

            changed = true;

            return {
              ...node,
              isRestricted: true,
              children: nextChildren,
            };
          });

          return changed ? updatedTree : tree;
        };

        /**
         * Check whether the entity folder and all folders under the entity
         * currently contain all site users.
         *
         * This preserves the existing entity forceAll calculation.
         */
        const folderTreeHasAllUsers = (
          tree: FolderTree
        ): boolean => {
          if (!tree?.length) {
            return true;
          }

          const stack: FolderNode[] = [...tree];

          while (stack.length > 0) {
            const node = stack.pop()!;

            if (!hasAllSiteUsers(node.users)) {
              return false;
            }

            const children =
              node.children ?? [];

            for (
              let index = 0;
              index < children.length;
              index += 1
            ) {
              stack.push(children[index]);
            }
          }

          return true;
        };

        let updatedEntity: Entity | undefined;

        const nextEntities = entities.map(entity => {
          if (
            Number(entity.entityId) !==
            Number(entityId)
          ) {
            return entity;
          }

          const entityHasAllUsers =
            hasAllSiteUsers(entity.userList);

          const foldersHaveAllUsers =
            folderTreeHasAllUsers(
              entity.folderTree ?? []
            );

          /*
           * Preserve the existing entity restriction rule.
           */
          const forceAll =
            entityHasAllUsers &&
            foldersHaveAllUsers;

          const nextFolderTree =
            updateEntityFolderTree(
              entity.folderTree ?? [],
              forceAll
            );

          const nextEntity: Entity = {
            ...entity,
            userList: forceAll
              ? allUsers
              : entity.userList,
            numberOfUsers: forceAll
              ? allUsers.length
              : entity.numberOfUsers,
            isRestricted: !forceAll,
            folderTree: nextFolderTree,
          };

          updatedEntity = nextEntity;

          return nextEntity;
        });

        const rootFolder = folderPermission[0];

        const nextFolderPermission: FolderTree =
          rootFolder && updatedEntity
            ? [
              {
                ...rootFolder,
                children: (
                  rootFolder.children ?? []
                ).map(node => {
                  if (
                    Number(node.entityId) !==
                    Number(entityId)
                  ) {
                    return node;
                  }

                  const updatedEntityRoot =
                    updatedEntity?.folderTree?.[0];

                  if (!updatedEntityRoot) {
                    return node;
                  }

                  /*
                   * Synchronize only the matching entity folder. Other
                   * entity branches and root-level custom folders remain
                   * unchanged.
                   */
                  return {
                    ...node,
                    isRestricted:
                      updatedEntityRoot.isRestricted,
                    users:
                      updatedEntityRoot.users ??
                      node.users,
                    usersCount:
                      updatedEntityRoot.usersCount ??
                      node.usersCount,
                    children:
                      updatedEntityRoot.children ??
                      node.children ??
                      [],
                  };
                }),
              },
            ]
            : folderPermission;

        set(legalEntitiesState, {
          ...entitiesState,
          entities: nextEntities,
          folderPermission:
            nextFolderPermission,
        });
      },
    []
  );
  
  const revalidateAllEntitiesIsRestricted = useRecoilCallback(
    ({ set, snapshot }) =>
      async () => {
        const entitiesState = await snapshot.getPromise(
          legalEntitiesState
        );

        const entities: Entity[] =
          entitiesState?.entities ?? [];

        const folderPermission: FolderTree =
          entitiesState?.folderPermission ?? [];

        const allUsers: string[] = (
          entitiesState?.usersPerSite ?? []
        ).map(user => String(user.userId));

        const siteUserSet = new Set(allUsers);
        const siteCount = siteUserSet.size;

        const isCustomFolder = (
          node: FolderNode
        ): boolean => Number(node.entityId) === 0;

        const uniqueLen = (
          users:
            | Array<string | number>
            | undefined
            | null
        ): number =>
          new Set((users ?? []).map(String)).size;

        const hasAllSiteUsers = (
          users:
            | Array<string | number>
            | undefined
            | null
        ): boolean => {
          const userSet = new Set(
            (users ?? []).map(String)
          );

          return (
            userSet.size === siteUserSet.size &&
            allUsers.every(userId =>
              userSet.has(userId)
            )
          );
        };

        /**
         * Preserve the existing entity validation rule.
         *
         * The entity is unrestricted only when the entity and every folder
         * in its folder tree contain all site users.
         */
        const folderTreeHasAllCounts = (
          tree: FolderTree
        ): boolean => {
          if (!tree?.length) {
            return true;
          }

          const stack: FolderNode[] = [...tree];

          while (stack.length > 0) {
            const node = stack.pop()!;

            if (uniqueLen(node.users) !== siteCount) {
              return false;
            }

            const children = node.children ?? [];

            for (
              let index = 0;
              index < children.length;
              index += 1
            ) {
              stack.push(children[index]);
            }
          }

          return true;
        };

        /**
         * Apply different restriction rules based on folder type.
         *
         * Entity folder:
         * - Uses the existing forceAll behavior.
         * - When forceAll is true, users are reset to all site users.
         * - When forceAll is false, the entity folder is restricted.
         *
         * Custom folder:
         * - Uses its actual folder users.
         * - Is restricted when it is missing users or when its nearest
         *   custom-folder ancestor is restricted.
         * - Its users are never replaced by the entity forceAll logic.
         */
        const updateFolderTree = (
          tree: FolderTree,
          forceAll: boolean,
          ancestorCustomRestricted = false
        ): FolderTree => {
          return (tree ?? []).map(node => {
            if (isCustomFolder(node)) {
              const currentFolderRestricted =
                ancestorCustomRestricted ||
                !hasAllSiteUsers(node.users);

              return {
                ...node,
                usersCount: uniqueLen(node.users),
                isRestricted: currentFolderRestricted,
                children: updateFolderTree(
                  node.children ?? [],
                  forceAll,
                  currentFolderRestricted
                ),
              };
            }

            /*
             * Entity folders do not inherit a custom-folder restriction.
             *
             * If custom folders exist below an entity folder, their custom
             * ancestor chain starts again from false.
             */
            const children = updateFolderTree(
              node.children ?? [],
              forceAll,
              false
            );

            if (forceAll) {
              return {
                ...node,
                users: allUsers,
                usersCount: siteCount,
                isRestricted: false,
                children,
              };
            }

            return {
              ...node,
              isRestricted: true,
              children,
            };
          });
        };

        const nextEntities = entities.map(entity => {
          const entityHasAll =
            uniqueLen(entity.userList) === siteCount;

          const foldersHaveAll =
            folderTreeHasAllCounts(
              entity.folderTree ?? []
            );

          /*
           * Preserve the original entity-level calculation.
           */
          const forceAll =
            entityHasAll && foldersHaveAll;

          return {
            ...entity,

            /*
             * Preserve original entity behavior.
             */
            userList: forceAll
              ? allUsers
              : entity.userList,

            numberOfUsers: forceAll
              ? allUsers.length
              : entity.numberOfUsers,

            isRestricted: !forceAll,

            /*
             * Entity and custom folders are handled independently inside
             * updateFolderTree.
             */
            folderTree: updateFolderTree(
              entity.folderTree ?? [],
              forceAll
            ),
          };
        });

        const entityMap = new Map(
          nextEntities.map(entity => [
            Number(entity.entityId),
            entity,
          ])
        );

        const rootFolder = folderPermission[0];

        const nextFolderPermission: FolderTree =
          rootFolder
            ? [
              {
                ...rootFolder,
                children: (
                  rootFolder.children ?? []
                ).map(node => {
                  /*
                   * A root-level custom folder is not an entity folder,
                   * so it must not be synchronized from an entity.
                   */
                  if (isCustomFolder(node)) {
                    return node;
                  }

                  const updatedEntity =
                    entityMap.get(
                      Number(node.entityId)
                    );

                  const updatedEntityRoot =
                    updatedEntity?.folderTree?.[0];

                  if (
                    !updatedEntity ||
                    !updatedEntityRoot
                  ) {
                    return node;
                  }

                  /*
                   * Synchronize only this matching entity folder.
                   * Other entity folders and root custom folders keep
                   * their existing values and object references.
                   */
                  return {
                    ...node,
                    isRestricted:
                      updatedEntityRoot.isRestricted,

                    users:
                      updatedEntityRoot.users ??
                      node.users,

                    usersCount:
                      updatedEntityRoot.usersCount ??
                      node.usersCount,

                    children:
                      updatedEntityRoot.children ??
                      node.children ??
                      [],
                  };
                }),
              },
            ]
            : folderPermission;

        set(legalEntitiesState, {
          ...entitiesState,
          entities: nextEntities,
          folderPermission:
            nextFolderPermission,
        });
      },
    []
  );

  return {
    loadFromApi,
    updateIsButtonDisabled,
    updateFolderPermission,
    updateEntityUserList,
    updateEntityUserListFolderPermission,
    updateEntityFolderTree,
    updateSiteFolderPermission,
    revalidateEntityIsRestricted,
    revalidateAllEntitiesIsRestricted,
    resetEntitiesToSnapshot,
    resetEntitiesState,
    loadFromRefreshFolderPermissions
  };
};
